import BigNumber from 'bignumber.js'
import { ChainIdEnum } from 'config/constants/network'
import { RouteConfig } from 'config/constants/route'
import { CashbackTransaction } from 'config/types/cashback'
import { ProfileTierInfo } from 'config/types/profile'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import useModal from 'hooks/useModal'
import Column, { ColumnCenter } from 'layout/Components/Column'
import { RowBetween, RowCenter, RowMiddle } from 'layout/Components/Row'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useTiers, useTokenDetails } from 'state/app/hooks'
import { useCashbackBonus } from 'state/bonus/hooks/cashbackBonus'
import { useProfileTier } from 'state/profile/hooks'
import { Icons } from 'svgs'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image/Image'
import Link from 'UIKit/Link/Link'
import { ModalProps } from 'UIKit/Modal'
import Text from 'UIKit/Text/Text'
import TokenLogo from 'UIKit/TokenLogo'
import { timeLeftUnit } from 'utils'
import { getFullDisplayBalance } from 'utils/formatBalance'
import CashbackBonusInfo from './CashbackBonus'
import { useCashbackFeature } from './hooks'
import {
  ModalWrapper,
  ScaleVariantBonus,
  StyledArrowButton,
  StyledButton,
  StyledCashbackCountdownContent,
  StyledTag,
  StyledTitle,
  Wrapper,
  WrapperContentDropdown,
} from './styled'

const CashbackItem: React.FC<{ transaction: CashbackTransaction } & BoxProps> = ({ transaction, ...props }) => {
  const token = useTokenDetails(ChainIdEnum[transaction.network] as any, transaction.currency)

  return token ? (
    <RowMiddle minHeight="20px" width="100%" {...props}>
      <TokenLogo token={token} width="20px" height="20px" />
      <Text fontSize="14px" ml="8px" mr="4px" fontWeight="600">
        {getFullDisplayBalance(new BigNumber(transaction?.value), 0, 4)}
      </Text>
      <Text fontWeight="600" fontSize="14px" color="textSubtle">
        {token?.name}
      </Text>
    </RowMiddle>
  ) : null
}

const LevelCashback: React.FC<{ tier: ProfileTierInfo; title: string; isModal?: boolean } & BoxProps> = ({
  tier,
  title,
  isModal,
  ...props
}) => {
  return (
    <Flex {...props}>
      <Image src={tier.logoUrl} width={isModal ? 32 : 24} height={isModal ? 32 : 24} />
      <Column ml="8px">
        <Text color="textAlt1" fontSize={['12px', '', '10px']}>
          {title}
        </Text>
        <RowMiddle mt="8px">
          <Text bold fontSize={['16px', '', '12px']}>
            {tier.mainCashbackPercentage}%
          </Text>
          <Text color="textTertiary" fontSize={['16px', '', '12px']} ml="4px">
            <Trans>Cashback</Trans>
          </Text>
        </RowMiddle>
      </Column>
    </Flex>
  )
}

const CashbackContentCountdown: React.FC<{
  cashbackExpireTimeDetailRef: any
  isModal?: boolean
  onDismiss?: () => void
}> = ({ cashbackExpireTimeDetailRef, isModal, onDismiss = () => {} }) => {
  const { t } = useTranslation()

  const userTier = useProfileTier()
  const tiers = useTiers()
  const userTierIndex = useMemo(() => tiers.findIndex((tier) => tier.id === userTier?.id), [tiers, userTier])

  const userNextTier = useMemo(
    () => tiers[userTierIndex < tiers.length ? userTierIndex + 1 : null],
    [tiers, userTierIndex],
  )

  return (
    <Column>
      <StyledCashbackCountdownContent ref={cashbackExpireTimeDetailRef} />

      <Column position="relative">
        {userTier && (
          <LevelCashback isModal={isModal} tier={userTier} title={t('Current Level')} mt={['20px', '', '16px']} />
        )}
        {userNextTier && (
          <>
            <Box position="relative" width={['32px', '', '24px']}>
              <RowCenter
                position="absolute"
                top="-2px"
                left="50%"
                style={{
                  transform: 'translateX(-50%)',
                }}
              >
                <Icons.DashedLineIcon height="20px" />
              </RowCenter>
            </Box>
            <LevelCashback isModal={isModal} tier={userNextTier} title={t('Next Level')} mt={['20px', '', '16px']} />
          </>
        )}
      </Column>

      <Column width="100%">
        <StyledButton
          fontSize={['14px', '', '12px']}
          as={Link}
          href={RouteConfig.Cashback}
          width={['100%', '', 'max-content']}
          mt={['40px', '', '16px']}
          primary={isModal}
          onClick={onDismiss}
        >
          <RowCenter width="100%" justifyContent={['center !important', '', 'flex-start !important']}>
            <Trans>My Cashback</Trans>
          </RowCenter>
        </StyledButton>
      </Column>
    </Column>
  )
}

const CashbackContentDetail: React.FC<{
  availableCashbacks: CashbackTransaction[]
  cashbackExpireTimeDetailRef: any
  isAvailableCashback: boolean
  handleClaimCashback: () => void
  isModal?: boolean
  onDismiss?: () => void
}> = ({
  availableCashbacks,
  cashbackExpireTimeDetailRef,
  isAvailableCashback,
  handleClaimCashback,
  isModal,
  onDismiss = () => {},
}) => {
  const { inPeriodCashbackBonus, activeBonus, bonusCountdownRef, activeBonusInCashbackDuration } = useCashbackBonus()

  if (activeBonusInCashbackDuration) {
    return (
      <Column style={{ gap: '20px' }} width="100%">
        <StyledCashbackCountdownContent ref={bonusCountdownRef} />
        <CashbackBonusInfo bonus={activeBonus} onDismiss={onDismiss} />
      </Column>
    )
  }

  if (inPeriodCashbackBonus) {
    return (
      <Column style={{ gap: '20px' }} width="100%">
        <CashbackBonusInfo bonus={inPeriodCashbackBonus} onDismiss={onDismiss} />
      </Column>
    )
  }

  return isAvailableCashback ? (
    <>
      <Column>
        <StyledTitle>
          <Trans>Available cashback</Trans>
        </StyledTitle>
        {availableCashbacks.length > 0 ? (
          <Column my="6px">
            {availableCashbacks.map((transaction) => (
              <CashbackItem key={`cashback-item-${transaction.id}`} transaction={transaction} my="6px" />
            ))}
          </Column>
        ) : (
          <ColumnCenter my="12px">
            <Text mt="12px" fontSize="12px" color="textSubtle">
              <Trans>No available cashback</Trans>
            </Text>
          </ColumnCenter>
        )}
      </Column>
      {availableCashbacks.length > 0 && (
        <Column mt={['28px', '', '0']} width="100%">
          <StyledButton
            style={{
              fontWeight: 'bold',
            }}
            fontSize={['14px', '', '12px']}
            primary
            onClick={() => {
              handleClaimCashback()
              onDismiss()
            }}
          >
            <Trans>Claim all</Trans>
          </StyledButton>
          <StyledButton
            style={{
              justifyContent: 'center',
              fontWeight: 'bold',
            }}
            fontSize={['14px', '', '12px']}
            mt="8px"
            as={Link}
            onClick={onDismiss}
            href={RouteConfig.Cashback}
          >
            <Trans>My Cashback</Trans>
          </StyledButton>
        </Column>
      )}
    </>
  ) : (
    <CashbackContentCountdown
      cashbackExpireTimeDetailRef={cashbackExpireTimeDetailRef}
      isModal={isModal}
      onDismiss={onDismiss}
    />
  )
}

export const CashbackContent: React.FC<{
  availableCashbacks: CashbackTransaction[]
  cashbackExpireTimeDetailRef: any
  isAvailableCashback: boolean
  handleClaimCashback: () => void
}> = ({ availableCashbacks, cashbackExpireTimeDetailRef, isAvailableCashback, handleClaimCashback }) => {
  return (
    <WrapperContentDropdown>
      <CashbackContentDetail
        availableCashbacks={availableCashbacks}
        cashbackExpireTimeDetailRef={cashbackExpireTimeDetailRef}
        isAvailableCashback={isAvailableCashback}
        handleClaimCashback={handleClaimCashback}
      />
    </WrapperContentDropdown>
  )
}

export const CashbackModal: React.FC<ModalProps> = ({ onDismiss = () => {}, ...props }) => {
  const { cashbackExpireTimeDetailRef, isAvailableCashback, availableCashbacks, handleClaimCashback } =
    useCashbackFeature()
  const { activeBonusInCashbackDuration } = useCashbackBonus()

  return (
    <ModalWrapper title={activeBonusInCashbackDuration ? 'Cashback' : 'Next Cashback'} onDismiss={onDismiss} {...props}>
      <CashbackContentDetail
        availableCashbacks={availableCashbacks}
        cashbackExpireTimeDetailRef={cashbackExpireTimeDetailRef}
        isAvailableCashback={isAvailableCashback}
        handleClaimCashback={handleClaimCashback}
        isModal
        onDismiss={onDismiss}
      />
    </ModalWrapper>
  )
}

const Cashback: React.FC<ScaleVariantBonus & BoxProps> = ({ tagButtonEnable, ...props }) => {
  const { t } = useTranslation()
  const { isDesktop } = useMatchBreakpoints()
  const { activeBonus, activeBonusInCashbackDuration } = useCashbackBonus()
  const time = timeLeftUnit(activeBonus?.condition?.expiryDate)

  const {
    cashbackExpireTimeDetailRef,
    cashbackExpireTimePreview,
    sumFiatAmountCashback,
    isAvailableCashback,
    availableCashbacks,
    handleClaimCashback,
    isDisabledByInPeriodCashback,
  } = useCashbackFeature()

  const [onPresent] = useModal(CashbackModal)

  const displayAvailableCashback = useMemo(() => {
    return (
      <>
        <RowMiddle>
          <Box width="24px">
            <Image src="/images/bonus/cashback-bonus.png" width={24} height={24} mr="4px" />
          </Box>
          {isAvailableCashback ? (
            <Text
              style={{
                whiteSpace: 'nowrap',
              }}
              fontSize={['14px', '', '12px']}
              bold
            >
              ${getFullDisplayBalance(sumFiatAmountCashback, 0, 4)}
            </Text>
          ) : (
            <Text
              fontSize={['14px', '', '12px']}
              style={{
                whiteSpace: 'nowrap',
              }}
              bold
            >
              {cashbackExpireTimePreview}
            </Text>
          )}
        </RowMiddle>

        {isAvailableCashback && (
          <StyledTag display={tagButtonEnable ? 'flex !important' : 'none !important'} ml="16px">
            <Trans>Available</Trans>
          </StyledTag>
        )}
      </>
    )
  }, [isAvailableCashback, sumFiatAmountCashback, cashbackExpireTimePreview, tagButtonEnable])

  const displayDisableByInPeriodCashback = useMemo(() => {
    return (
      <>
        <RowMiddle>
          <Image src="/images/bonus/cashback-bonus.png" width={24} height={24} />
          {activeBonusInCashbackDuration ? (
            <Text
              fontSize={['14px', '', '12px']}
              style={{
                whiteSpace: 'nowrap',
              }}
              bold
              wordBreak="keep-all"
              whiteSpace="nowrap"
              ml="4px"
            >
              {time} 🚀
            </Text>
          ) : (
            <Text
              fontSize={['14px', '', '12px']}
              style={{
                whiteSpace: 'nowrap',
              }}
              bold
              wordBreak="keep-all"
              whiteSpace="nowrap"
              ml="4px"
            >
              <Trans>Disabled</Trans>⚠️
            </Text>
          )}
        </RowMiddle>
      </>
    )
  }, [isAvailableCashback, sumFiatAmountCashback, cashbackExpireTimePreview, activeBonus, time])

  return (
    <Wrapper
      dropdownPosition="bottom-right"
      dropdownContent={
        isDesktop && (
          <CashbackContent
            availableCashbacks={availableCashbacks}
            cashbackExpireTimeDetailRef={cashbackExpireTimeDetailRef}
            isAvailableCashback={isAvailableCashback}
            handleClaimCashback={handleClaimCashback}
          />
        )
      }
      onClick={() => {
        if (!isDesktop) {
          onPresent()
        }
      }}
      title={isAvailableCashback || activeBonusInCashbackDuration ? t('Cashback') : t('Next Cashback')}
      style={{
        cursor: 'auto',
      }}
      {...props}
    >
      <RowBetween>
        <Flex width="100%" alignItems="center" justifyContent={['flex-start', '', 'space-between']} pb="4px">
          {isDisabledByInPeriodCashback ? displayDisableByInPeriodCashback : displayAvailableCashback}
        </Flex>

        <StyledArrowButton ml="8px" disabled />
      </RowBetween>
    </Wrapper>
  )
}

export default Cashback
